import { useEffect, useState } from "react";
import { ExternalLink, Linkedin } from "lucide-react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * NotableAlumni — a portrait strip on the Overview tab so families
 * can quickly see who studied at this university and where they
 * landed. Reads from `/api/universities/{id}/notable-alumni`.
 *
 * Missing portrait URLs render a monogram tile so the strip stays
 * uniform. Every card links out to Wikipedia when a `wiki` URL is
 * provided.
 *
 * Hides gracefully when we don't have a curated seed for the uni.
 */
export default function NotableAlumni({ uniId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/notable-alumni`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const rows = data?.alumni || [];
  const linkedInUrl = data?.linkedin_alumni_url;
  const linkedInSource = data?.linkedin_alumni_source;
  if (rows.length === 0 && !linkedInUrl) return null;

  return (
    <section className="mb-8" data-testid="notable-alumni">
      <div>
        <TabKicker>02 · Notable Alumni</TabKicker>
        <TabSectionTitle>Who walked these halls</TabSectionTitle>
      </div>

      {rows.length > 0 && (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 gap-3">
        {rows.map((a) => (
          <a
            key={a.name}
            href={a.wiki || "#"}
            target="_blank" rel="noopener noreferrer"
            className="flex gap-3 items-start p-3 border border-[#002147]/10 bg-white/40 hover:bg-white/80 hover:border-[#7C3AED] transition-colors group"
            data-testid={`alumni-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="shrink-0 w-14 h-14 bg-[#F3F1EC] border border-[#002147]/10 overflow-hidden">
              {a.portrait ? (
                <img
                  src={a.portrait}
                  alt={a.name}
                  className="w-full h-full object-cover grayscale-[35%]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-serif text-2xl text-[#002147]/40">
                  {a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-serif text-sm text-[#002147] leading-tight group-hover:text-[#7C3AED] transition-colors">
                {a.name}
              </div>
              <div className="mt-1 font-mono text-[9px] uppercase tracking-widest text-[#002147]/50">
                {a.subject} · {a.grad_year}
              </div>
              <div className="mt-1 font-sans text-xs text-[#002147]/75 leading-snug">
                {a.known_for}
              </div>
            </div>
            {a.wiki && (
              <ExternalLink className="h-3 w-3 shrink-0 text-[#002147]/30 group-hover:text-[#7C3AED] transition-colors" />
            )}
          </a>
        ))}
      </div>
      )}
      {linkedInUrl && (
        <a
          href={linkedInUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="notable-alumni-linkedin-cta"
          data-linkedin-source={linkedInSource}
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#0A66C2]/25 bg-white/70 hover:bg-[#0A66C2]/5 hover:border-[#0A66C2] px-4 py-2 text-[13px] text-[#0A66C2] transition-colors"
        >
          <Linkedin className="h-4 w-4" />
          <span>See alumni on LinkedIn</span>
          <ExternalLink className="h-3 w-3 opacity-60" />
        </a>
      )}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
        Editorial selection · portraits under CC / public domain via Wikimedia
      </p>
    </section>
  );
}
